import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MoreVertical, Plus, Pencil, Search, ToggleLeft, ToggleRight, Building2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { apiFetch } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { SettingsMenu } from "@/components/Holizontal/SettingsMenu";

function clsx(...s: Array<string | false | undefined | null>) {
  return s.filter(Boolean).join(" ");
}

function useDebouncedValue<T>(value: T, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/** Auto infinite-scroll sentinel (like Users) */
function useInfiniteSentinel(opts: { enabled: boolean; onLoadMore: () => void; root?: Element | null }) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!opts.enabled) return;
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) opts.onLoadMore();
      },
      { root: opts.root ?? null, rootMargin: "420px", threshold: 0.01 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [opts.enabled, opts.onLoadMore, opts.root]);

  return sentinelRef;
}

type BranchRow = {
  id: number;
  name: string;
  status: number; // 1 active, 0 suspended
};

type CursorResponse<T> = {
  data: T[];
  meta: { next_cursor: string | null; prev_cursor?: string | null; has_more: boolean; per_page?: number };
  anchor_id?: number | null;
};

type BranchFormState = {
  id?: number | null;
  name: string;
};

function StatusPill({ status, t }: { status: number; t: (k: string) => string }) {
  const active = status === 1;
  return (
    <span
      className={clsx(
        "inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold",
        active ? "bg-emerald-500/15 text-emerald-600" : "bg-rose-500/15 text-rose-600"
      )}
    >
      {active ? t("common.active") : t("common.suspended")}
    </span>
  );
}

function BranchForm({
  form,
  setForm,
  loading,
  onSubmit,
  isEdit,
  t,
}: {
  form: BranchFormState;
  setForm: React.Dispatch<React.SetStateAction<BranchFormState>>;
  loading?: boolean;
  onSubmit: () => void;
  isEdit?: boolean;
  t: (k: string) => string;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-4"
    >
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">{t("branches.name")}</label>
        <Input
          className="rounded-xl"
          value={form.name}
          onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
          placeholder={t("branches.name_placeholder")}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button
          type="submit"
          className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
          disabled={!!loading}
        >
          {loading ? t("common.loading") : isEdit ? t("common.update") : t("common.create")}
        </Button>
      </div>
    </form>
  );
}

export default function BranchesPage() {
  const qc = useQueryClient();
  const { t } = useTranslation();

  const dir = (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const rtl = dir === "rtl";

  // attach sentinel to Layout main scroll container (best)
  const mainEl = useMemo(() => document.querySelector("main"), []);

  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 350);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const [createForm, setCreateForm] = useState<BranchFormState>({ name: "" });
  const [editForm, setEditForm] = useState<BranchFormState>({ id: null, name: "" });

  // =========================
  // FETCH (CURSOR)
  // =========================
  const branchesInf = useInfiniteQuery({
    queryKey: ["branches_cursor", qDebounced],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (pageParam) params.set("cursor", String(pageParam));
      if (qDebounced?.trim()) params.set("q", qDebounced.trim());
      return apiFetch<CursorResponse<BranchRow>>(`/settings/branches?${params.toString()}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last?.meta?.has_more ? last.meta.next_cursor : null),
    refetchOnWindowFocus: false,
    staleTime: 10_000,
  });

  const pages = branchesInf.data?.pages ?? [];
  const branches = useMemo(() => pages.flatMap((p) => p.data || []), [pages]);
  const lastMeta = pages.length ? pages[pages.length - 1]?.meta : undefined;

  const loadingFirst = branchesInf.isLoading;
  const loadingMore = branchesInf.isFetchingNextPage;

  // =========================
  // AUTO LOAD MORE
  // =========================
  const sentinelRef = useInfiniteSentinel({
    enabled: !!branchesInf.hasNextPage && !branchesInf.isFetchingNextPage && !branchesInf.isLoading,
    onLoadMore: () => {
      if (branchesInf.hasNextPage && !branchesInf.isFetchingNextPage) branchesInf.fetchNextPage();
    },
    root: (mainEl as Element) ?? null,
  });

  // =========================
  // CREATE (optimistic)
  // =========================
  const createMut = useMutation({
    mutationFn: async (payload: any) =>
      apiFetch("/settings/branches", {
        method: "POST",
        body: payload, // apiFetch should JSON.stringify + set headers
      }),
    onMutate: async (newBranch) => {
      await qc.cancelQueries({ queryKey: ["branches_cursor"] });
      const prev = qc.getQueryData<any>(["branches_cursor", qDebounced]);

      qc.setQueryData<any>(["branches_cursor", qDebounced], (old: any) => {
        if (!old?.pages?.length) return old;
        const first = old.pages[0];
        return {
          ...old,
          pages: [
            {
              ...first,
              data: [
                { id: Date.now(), name: newBranch.name, status: 1 },
                ...(first.data ?? []),
              ],
            },
            ...old.pages.slice(1),
          ],
        };
      });

      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["branches_cursor", qDebounced], ctx.prev);
      toast.error(t("common.failed"));
    },
    onSuccess: () => {
      toast.success(t("branches.created"));
      setCreateOpen(false);
      setCreateForm({ name: "" });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["branches_cursor"] }),
  });

  // =========================
  // EDIT (optimistic)
  // =========================
  const editMut = useMutation({
    mutationFn: async (payload: any) =>
      apiFetch(`/settings/branches/${payload.id}`, {
        method: "PUT",
        body: payload,
      }),
    onMutate: async (updated) => {
      await qc.cancelQueries({ queryKey: ["branches_cursor"] });
      const prev = qc.getQueryData<any>(["branches_cursor", qDebounced]);

      qc.setQueryData<any>(["branches_cursor", qDebounced], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((p: any) => ({
            ...p,
            data: (p.data ?? []).map((b: BranchRow) => (b.id === updated.id ? { ...b, ...updated } : b)),
          })),
        };
      });

      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["branches_cursor", qDebounced], ctx.prev);
      toast.error(t("branches.update_failed"));
    },
    onSuccess: () => {
      toast.success(t("branches.updated"));
      setEditOpen(false);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["branches_cursor"] }),
  });

  // =========================
  // STATUS TOGGLE (optimistic)
  // =========================
  const statusMut = useMutation({
    mutationFn: async (payload: { id: number; status: number }) =>
      apiFetch(`/settings/branches/${payload.id}/status`, {
        method: "PUT",
        body: payload,
      }),
    onMutate: async (payload) => {
      await qc.cancelQueries({ queryKey: ["branches_cursor"] });
      const prev = qc.getQueryData<any>(["branches_cursor", qDebounced]);

      qc.setQueryData<any>(["branches_cursor", qDebounced], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((p: any) => ({
            ...p,
            data: (p.data ?? []).map((b: BranchRow) => (b.id === payload.id ? { ...b, status: payload.status } : b)),
          })),
        };
      });

      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["branches_cursor", qDebounced], ctx.prev);
      toast.error(t("branches.status_failed"));
    },
    onSuccess: () => toast.success(t("branches.status_updated")),
    onSettled: () => qc.invalidateQueries({ queryKey: ["branches_cursor"] }),
  });

  // =========================
  // ACTIONS
  // =========================
  const openCreate = () => {
    setCreateForm({ name: "" });
    setCreateOpen(true);
  };

  const openEdit = (b: BranchRow) => {
    setEditForm({ id: b.id, name: b.name });
    setEditOpen(true);
  };

  const submitCreate = () => {
    if (!createForm.name.trim()) return toast.error(t("branches.name_required"));
    createMut.mutate({ name: createForm.name.trim() });
  };

  const submitEdit = () => {
    if (!editForm.id) return toast.error(t("common.failed"));
    if (!editForm.name.trim()) return toast.error(t("branches.name_required"));
    editMut.mutate({ id: editForm.id, name: editForm.name.trim() });
  };

  const toggleStatus = (b: BranchRow) => {
    const next = b.status === 1 ? 0 : 1;
    statusMut.mutate({ id: b.id, status: next });
  };

  // =========================
  // UI
  // =========================
  return (
    <div dir={dir} className="space-y-6">
      {/* Header */}

      <header className="border-b border-border bg-card sticky top-0 z-30 mb-3">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <Building2 className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight"> {t("branches.title")}</h1>
              <p className="text-xs text-muted-foreground">Infinite scroll </p>
            </div>
          </div>

          <Button
            onClick={openCreate}
            className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-xl shadow-[0_10px_30px_rgba(124,58,237,0.18)]"
            disabled={loadingFirst}
          >
            <Plus className={clsx("h-4 w-4", rtl ? "ml-2" : "mr-2")} />
            {t("common.create")}
          </Button>
        </div>
      </header>







      {branchesInf.isError ? (
        <Card className="shadow-card">
          <CardContent className="p-4 text-sm text-red-600">
            {(branchesInf.error as any)?.message || t("common.failed")}
          </CardContent>
        </Card>
      ) : null}

      <SettingsMenu />

      <Card className="shadow-card overflow-hidden">
        <CardHeader className="border-b border-border/60">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">


            {/* Search */}
            <div className="relative">
              <Search className={clsx("absolute top-2.5 h-4 w-4 text-muted-foreground", rtl ? "right-3" : "left-3")} />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("common.search")}
                className={clsx("w-full sm:w-72 rounded-xl", rtl ? "pr-10" : "pl-10")}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="w-full overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
                <tr className={clsx("text-left", rtl && "text-right")}>
                  <th className="p-3 whitespace-nowrap">#</th>
                  <th className="p-3 whitespace-nowrap">{t("branches.name")}</th>
                  <th className="p-3 whitespace-nowrap">{t("common.status")}</th>
                  <th className={clsx("p-3 whitespace-nowrap", rtl ? "text-left" : "text-right")}>{t("common.actions")}</th>
                </tr>
              </thead>

              <tbody>
                {loadingFirst ? (
                  <>
                    {Array.from({ length: 10 }).map((_, i) => (
                      <tr key={`sk-${i}`} className="border-b">
                        <td className="p-3 text-muted-foreground">—</td>
                        <td className="p-3"><div className="h-4 w-40 rounded bg-muted animate-pulse" /></td>
                        <td className="p-3"><div className="h-6 w-24 rounded bg-muted animate-pulse" /></td>
                        <td className={clsx("p-3", rtl ? "text-left" : "text-right")}><div className="inline-block h-8 w-10 rounded bg-muted animate-pulse" /></td>
                      </tr>
                    ))}
                  </>
                ) : branches.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-10 text-center text-muted-foreground">
                      {t("common.no_data")}
                    </td>
                  </tr>
                ) : (
                  branches.map((b, idx) => (
                    <tr key={b.id} className="border-b hover:bg-muted/20">
                      <td className="p-3 text-muted-foreground">{idx + 1}</td>
                      <td className="p-3 font-semibold">{b.name}</td>
                      <td className="p-3">
                        <StatusPill status={b.status} t={t} />
                      </td>

                      <td className={clsx("p-3", rtl ? "text-left" : "text-right")}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="rounded-xl">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>

                          <DropdownMenuContent align={rtl ? "start" : "end"} className="rounded-xl">
                            <DropdownMenuItem onClick={() => openEdit(b)}>
                              <Pencil className={clsx("h-4 w-4", rtl ? "ml-2" : "mr-2")} />
                              {t("common.edit")}
                            </DropdownMenuItem>

                            <DropdownMenuItem onClick={() => toggleStatus(b)}>
                              {b.status === 1 ? (
                                <ToggleLeft className={clsx("h-4 w-4", rtl ? "ml-2" : "mr-2")} />
                              ) : (
                                <ToggleRight className={clsx("h-4 w-4", rtl ? "ml-2" : "mr-2")} />
                              )}
                              {b.status === 1 ? t("branches.suspend") : t("branches.activate")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}

                {/* Loading more skeleton */}
                {loadingMore ? (
                  <>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <tr key={`more-${i}`} className="border-b">
                        <td className="p-3 text-muted-foreground">—</td>
                        <td className="p-3"><div className="h-4 w-40 rounded bg-muted animate-pulse" /></td>
                        <td className="p-3"><div className="h-6 w-24 rounded bg-muted animate-pulse" /></td>
                        <td className={clsx("p-3", rtl ? "text-left" : "text-right")}><div className="inline-block h-8 w-10 rounded bg-muted animate-pulse" /></td>
                      </tr>
                    ))}
                  </>
                ) : null}
              </tbody>
            </table>
          </div>

          {/* Sentinel for auto-load */}
          <div ref={sentinelRef} />

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 border-t border-border/60 p-4">
            <div className="text-sm text-muted-foreground">
              {t("common.showing")}{" "}
              <span className="font-medium text-foreground">{branches.length}</span>{" "}
              {lastMeta?.has_more ? t("branches.more_available") : t("branches.end")}
            </div>

            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => branchesInf.fetchNextPage()}
              disabled={!branchesInf.hasNextPage || branchesInf.isFetchingNextPage}
            >
              {branchesInf.isFetchingNextPage ? t("common.loading") : branchesInf.hasNextPage ? t("common.next") : t("branches.no_more")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* CREATE MODAL */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>{t("branches.create_title")}</DialogTitle>
          </DialogHeader>

          <BranchForm
            form={createForm}
            setForm={setCreateForm}
            loading={createMut.isPending}
            onSubmit={submitCreate}
            t={t}
          />
 <div className="border-t border-border px-5 py-4">
<DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => setCreateOpen(false)}
              disabled={createMut.isPending}
            >
              {t("common.cancel")}
            </Button>
          </DialogFooter>
 </div>
          
        </DialogContent>
      </Dialog>

      {/* EDIT MODAL */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>{t("branches.edit_title")}</DialogTitle>
          </DialogHeader>

          <BranchForm
            form={editForm}
            setForm={setEditForm}
            loading={editMut.isPending}
            onSubmit={submitEdit}
            isEdit
            t={t}
          />
<div className="border-t border-border px-5 py-4">
<DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => setEditOpen(false)}
              disabled={editMut.isPending}
            >
              {t("common.cancel")}
            </Button>
          </DialogFooter>
</div>
          
        </DialogContent>
      </Dialog>
    </div>
  );
}